import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Search, ImagePlus, X } from "lucide-react";
import { format } from "date-fns";

interface Event {
  id: string;
  title: string;
  type: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  description: string;
  external_link: string | null;
  is_special: boolean;
  chapter: "washington" | "union" | "all";
  created_by: string | null;
  image_url: string | null;
}

const emptyEvent = {
  title: "",
  type: "Special Event",
  date: "",
  start_time: "",
  end_time: "",
  location: "",
  description: "",
  external_link: "",
  is_special: true,
  chapter: "all" as "washington" | "union" | "all",
  category: "special" as "recurring" | "special",
};

const AdminEventList = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);
  const [flyerFile, setFlyerFile] = useState<File | null>(null);
  const [flyerPreview, setFlyerPreview] = useState<string | null>(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    const { data, error } = await supabase.from("events").select("*").order("date", { ascending: false });
    if (error) {
      toast.error("Error loading events");
      return;
    }
    setEvents(data || []);
    setLoading(false);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setIsCreating(false);
    setFlyerFile(null);
    setFlyerPreview(event.image_url || null);
    setFormData({
      title: event.title,
      type: event.type,
      date: event.date,
      start_time: event.start_time,
      end_time: event.end_time,
      location: event.location,
      description: event.description,
      external_link: event.external_link || "",
      is_special: event.is_special,
      chapter: event.chapter as "washington" | "union" | "all",
      category: event.is_special ? "special" : "recurring",
    });
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setIsCreating(true);
    setFlyerFile(null);
    setFlyerPreview(null);
    setFormData(emptyEvent);
  };

  const handleFlyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setFlyerFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFlyerPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeFlyerPreview = () => {
    setFlyerFile(null);
    setFlyerPreview(null);
  };

  const uploadFlyer = async (): Promise<string | null> => {
    if (!flyerFile) return flyerPreview; // keep existing if no new file
    const fileExt = flyerFile.name.split(".").pop();
    const filePath = `admin/${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from("event-flyers").upload(filePath, flyerFile);
    if (error) throw error;
    const { data: urlData } = supabase.storage.from("event-flyers").getPublicUrl(filePath);
    return urlData.publicUrl;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const imageUrl = await uploadFlyer();
      const payload = {
        title: formData.title,
        type: formData.type,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        location: formData.location,
        description: formData.description,
        external_link: formData.external_link || null,
        is_special: formData.category === "special",
        chapter: formData.chapter,
        image_url: imageUrl,
      };

      if (isCreating) {
        const { error } = await supabase.from("events").insert([{
          ...payload,
          created_by: session?.user.id || null,
        }]);
        if (error) throw error;
        toast.success("Event created successfully!");
      } else if (editingEvent) {
        const { error } = await supabase.from("events").update(payload).eq("id", editingEvent.id);
        if (error) throw error;
        toast.success("Event updated successfully!");
      }

      setEditingEvent(null);
      setIsCreating(false);
      setFlyerFile(null);
      setFlyerPreview(null);
      fetchEvents();
    } catch (error: any) {
      toast.error("Error saving event: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event: Event) => {
    if (!confirm(`Are you sure you want to delete "${event.title}"?`)) return;

    const { error } = await supabase.from("events").delete().eq("id", event.id);
    if (error) {
      toast.error("Error deleting event: " + error.message);
      return;
    }
    toast.success("Event deleted");
    fetchEvents();
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const filteredEvents = events.filter((e) =>
    e.title.toLowerCase().includes(search.toLowerCase()) ||
    e.location.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <p>Loading events...</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="hidden md:table-cell">Location</TableHead>
                <TableHead>Chapter</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">
                    {event.title}
                    <Badge variant={event.is_special ? "default" : "secondary"} className="ml-2 text-xs">
                      {event.is_special ? "Special" : "Recurring"}
                    </Badge>
                  </TableCell>
                  <TableCell>{format(new Date(event.date.replace(/-/g, '/') + " 00:00:00"), "MMM d, yyyy")}</TableCell>
                  <TableCell className="hidden md:table-cell">{event.location}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {event.chapter}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(event)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(event)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No events found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editingEvent || isCreating} onOpenChange={(open) => { if (!open) { setEditingEvent(null); setIsCreating(false); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isCreating ? "Create New Event" : `Edit: ${editingEvent?.title}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Event Title *</Label>
              <Input value={formData.title} onChange={(e) => handleChange("title", e.target.value)} required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recurring">Recurring</SelectItem>
                    <SelectItem value="special">Special Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Input value={formData.type} onChange={(e) => handleChange("type", e.target.value)} placeholder="e.g. Weekly Meeting, Gala" />
              </div>
              <div className="space-y-2">
                <Label>Chapter *</Label>
                <Select value={formData.chapter} onValueChange={(v) => handleChange("chapter", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Chapters</SelectItem>
                    <SelectItem value="washington">Washington</SelectItem>
                    <SelectItem value="union">Union</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={formData.date} onChange={(e) => handleChange("date", e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input type="time" value={formData.start_time} onChange={(e) => handleChange("start_time", e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input type="time" value={formData.end_time} onChange={(e) => handleChange("end_time", e.target.value)} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Location *</Label>
              <Input value={formData.location} onChange={(e) => handleChange("location", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea value={formData.description} onChange={(e) => handleChange("description", e.target.value)} rows={4} required />
            </div>
            <div className="space-y-2">
              <Label>Event Flyer (Optional)</Label>
              {flyerPreview ? (
                <div className="relative inline-block">
                  <img src={flyerPreview} alt="Flyer preview" className="max-h-64 rounded-lg border object-contain" />
                  <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2 h-7 w-7" onClick={removeFlyerPreview}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label htmlFor="admin-flyer-upload" className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 cursor-pointer hover:border-primary/50 transition-colors">
                  <ImagePlus className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Click to upload a flyer image</span>
                  <input id="admin-flyer-upload" type="file" accept="image/*" className="hidden" onChange={handleFlyerChange} />
                </label>
              )}
            </div>
            <div className="space-y-2">
              <Label>External Link</Label>
              <Input type="url" value={formData.external_link} onChange={(e) => handleChange("external_link", e.target.value)} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setEditingEvent(null); setIsCreating(false); }}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : isCreating ? "Create Event" : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventList;
